import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CheckSquare, Clock, ShieldCheck } from "lucide-react";

import { SiteShell } from "@/components/marketing/site-shell";
import { getLesson } from "@/lib/academy/curriculum";

export const Route = createFileRoute("/academy/$course/$lesson")({
  head: ({ params }) => {
    const found = getLesson(params.course, params.lesson);
    const title = found
      ? `${found.lesson.title} — ${found.course.short} | ArosFlow Academy`
      : "Lesson — ArosFlow Academy";
    const desc = found?.lesson.summary ?? "ArosFlow Academy lesson.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(found ? [] : [{ name: "robots", content: "noindex" }]),
      ],
    };
  },
  component: LessonPage,
});

function LessonPage() {
  const params = Route.useParams();
  const found = getLesson(params.course, params.lesson);

  if (!found) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
          <h1 className="text-2xl font-semibold">Lesson not found</h1>
          <Link to="/academy" className="focus-key mt-6 inline-block text-sm text-gold">
            Back to Academy
          </Link>
        </div>
      </SiteShell>
    );
  }

  const { course, lesson, prev, next, index } = found;

  return (
    <SiteShell>
      <article className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <Link
          to="/academy/$course"
          params={{ course: course.slug }}
          className="focus-key inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-gold"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> {course.short}
        </Link>

        <p className="mt-5 text-[10px] uppercase tracking-[0.25em] text-gold">
          Lesson {index + 1} of {course.lessons.length}
        </p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">{lesson.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{lesson.summary}</p>
        <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Clock className="h-3.5 w-3.5" aria-hidden /> {lesson.minutes} min read
        </p>

        <div className="mt-8 space-y-7">
          {lesson.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="text-sm font-semibold text-gold">{s.heading}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-2.5 text-sm leading-relaxed text-white/85">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>

        <section className="mt-9 rounded-2xl border border-[var(--glass-border)] bg-card p-5">
          <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
            <CheckSquare className="h-4 w-4 text-gold" aria-hidden /> Do this next
          </h2>
          <ul className="mt-3 space-y-2 text-xs leading-relaxed text-white/80">
            {lesson.actions.map((a) => (
              <li key={a} className="flex gap-2">
                <span className="text-gold" aria-hidden>
                  ·
                </span>
                {a}
              </li>
            ))}
          </ul>
          {lesson.tool ? (
            <Link
              to={lesson.tool.to}
              className="focus-key mt-4 inline-flex items-center gap-1.5 rounded-lg gradient-gold px-4 py-2 text-xs font-semibold text-black"
            >
              {lesson.tool.label} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          ) : null}
        </section>

        <nav
          aria-label="Lesson navigation"
          className="mt-8 flex flex-col gap-3 border-t border-[var(--glass-border)] pt-6 sm:flex-row sm:justify-between"
        >
          {prev ? (
            <Link
              to="/academy/$course/$lesson"
              params={{ course: course.slug, lesson: prev.slug }}
              className="focus-key inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-xs text-white/85 hover:bg-white/5"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> {prev.title}
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to="/academy/$course/$lesson"
              params={{ course: course.slug, lesson: next.slug }}
              className="focus-key inline-flex items-center gap-2 rounded-lg border border-[var(--gold)]/40 px-4 py-2 text-xs font-semibold text-gold hover:bg-white/5"
            >
              {next.title} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          ) : (
            <Link
              to="/academy/$course"
              params={{ course: course.slug }}
              className="focus-key inline-flex items-center gap-2 rounded-lg border border-[var(--gold)]/40 px-4 py-2 text-xs font-semibold text-gold hover:bg-white/5"
            >
              Course finished — back to lessons
            </Link>
          )}
        </nav>

        <p className="mt-8 flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" aria-hidden />
          Education only — not legal, tax or investment advice, and no promise of income. Rules
          differ by state; check yours and use a real estate attorney.
        </p>
      </article>
    </SiteShell>
  );
}
